import React from 'react';
import ClockIcon from '@shared/assets/icons/v2/clock.svg';
import GifIcon from '@shared/assets/icons/v2/gif.svg';
import ImagestackIcon from '@shared/assets/icons/v2/imagestack.svg';
import PlusCircleFilledIcon from '@shared/assets/icons/v2/plus-circle-filled.svg';
import PlusCircleIcon from '@shared/assets/icons/v2/plus-circle.svg';
import { MBTooltip } from '@shared/components/tooltip';
import { MBTouchableIcon } from '@shared/components/touchable';
import { MBSelectAttachmentType } from '@shared/scenes/upload/util/types';
import { Colors } from '@shared/styles';
import { MBAnalytics } from '@shared/util/analytics';
import { i18n } from '@shared/util/i18n';
import { MBOverlay } from '@shared/util/overlay';
import { MBPlusButtonActionRow } from './actionRow';

interface MBPlusButtonProps {
  draftId: string;
  urgent: boolean;
  hideUrgent?: boolean;
  onShowGIFModal: () => void;
  onSelectAttachment: (type: MBSelectAttachmentType) => void;
  onSendAsUrgent: () => void;
  onRemoveSendAsUrgent: () => void;
}

interface MBPlusButtonState {
  showingActions: boolean;
}

export class MBPlusButton extends React.PureComponent<MBPlusButtonProps, MBPlusButtonState> {
  state: MBPlusButtonState = { showingActions: false };

  onSendAsUrgent = () => {
    this.hideActions();
    // Note(fant): on web the urgent info modal will be
    // auto closed if this happens sync. If we show it
    // in the next tick, react-modal won't close immediately
    setTimeout(this.props.onSendAsUrgent);
    MBAnalytics.track('Chat::Input::PlusButton::SendAsUrgent', { draftId: this.props.draftId });
  };

  onRemoveSendAsUrgent = () => {
    this.hideActions();
    this.props.onRemoveSendAsUrgent();
    MBAnalytics.track('Chat::Input::PlusButton::RemoveSendAsUrgent', {
      draftId: this.props.draftId,
    });
  };

  onShowGIFModal = () => {
    this.props.onShowGIFModal();
    this.hideActions();
    MBAnalytics.track('Chat::Input::PlusButton::ShowGIFModal', { draftId: this.props.draftId });
  };

  onSelectAttachment = () => {
    this.props.onSelectAttachment('all');
    this.hideActions();
    MBAnalytics.track('Chat::Input::PlusButton::SelectAttachment', { draftId: this.props.draftId });
  };

  hideActions = () => {
    MBOverlay.hide();
    this.setState({ showingActions: false });
  };

  showActions = () => {
    this.setState({ showingActions: true });
    MBOverlay.show({
      parent: this,
      closeOnBackdropPress: true,
      onClose: this.hideActions,
      position: ({ bottom, height, left }) => ({
        left,
        bottom: window.innerHeight - bottom + height - window.scrollY * 2,
        position: 'absolute',
      }),
      component: () => (
        <MBTooltip padding={0} verticalArrowAlignment="below">
          <MBPlusButtonActionRow
            title={i18n('chat.input.plusButton.addImageOrFile')}
            icon={ImagestackIcon}
            onPress={this.onSelectAttachment}
          />
          <MBPlusButtonActionRow
            title={i18n('chat.input.plusButton.gifSearch')}
            icon={GifIcon}
            onPress={this.onShowGIFModal}
          />

          {!this.props.hideUrgent && !this.props.urgent && (
            <MBPlusButtonActionRow
              title={i18n('chat.input.plusButton.sendAsUrgent')}
              icon={ClockIcon}
              onPress={this.onSendAsUrgent}
            />
          )}

          {!this.props.hideUrgent && !!this.props.urgent && (
            <MBPlusButtonActionRow
              title={i18n('chat.input.plusButton.removeSendAsUrgent')}
              icon={ClockIcon}
              onPress={this.onRemoveSendAsUrgent}
              color={Colors.v2.red}
            />
          )}
        </MBTooltip>
      ),
    });
  };

  toggleActions = () => {
    if (this.state.showingActions) {
      this.hideActions();
    } else {
      this.showActions();
      MBAnalytics.track('Chat::Input::PlusButton::Show', { draftId: this.props.draftId });
    }
  };

  render() {
    return (
      <MBTouchableIcon
        onPress={this.toggleActions}
        color={Colors.v2.purple}
        noPadding
        source={this.state.showingActions ? PlusCircleFilledIcon : PlusCircleIcon}
      />
    );
  }
}
